import { useRoute, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { documentSharingService } from "@/services/documentSharingService";
import { PublicDocumentShareView } from "@/components/PublicDocumentShareView";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Loader2 } from "lucide-react";

export default function PublicDocumentSharePage() {
  const [, params] = useRoute("/documents/share/:token");
  const token = params?.token || "";

  const { data: validation, isPending, isFetching, isFetched } = useQuery({
    queryKey: ["/api/documents/share/validate", token],
    queryFn: () => documentSharingService.validatePublicLink(token),
    enabled: !!token,
    retry: false,
  });

  const loginHref = `/login?redirect=${encodeURIComponent(
    typeof window !== "undefined" ? window.location.pathname : `/documents/share/${token}`
  )}`;

  // Route params and the validate RPC resolve async; avoid flashing "Link unavailable" before fetch runs.
  const isResolving = !token || isPending || isFetching || !isFetched;

  if (isResolving) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!validation?.isValid || !validation.document) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Link unavailable
            </CardTitle>
            <CardDescription>
              {validation?.message || "This share link is invalid or has expired."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="border-b px-4 py-2 flex items-center justify-between gap-2 shrink-0">
        <span className="text-sm font-medium text-muted-foreground">Deeply Smart</span>
        <Button variant="outline" size="sm" asChild>
          <Link href={loginHref}>Sign in</Link>
        </Button>
      </div>
      <PublicDocumentShareView document={validation.document} />
    </div>
  );
}
